import { useState, useEffect } from "react";
import { T } from "./theme";
import { Trophy, TrendingUp, TrendingDown, BarChart2, Building2 } from "lucide-react";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import PageLoading from "./components/PageLoading";
import Card from "./components/Card";
import Badge from "./components/Badge";

const isMe = u => window.usuarioUnidade ? u?.toLowerCase().includes(window.usuarioUnidade.toLowerCase().split(' ')[0]) : u?.toLowerCase().includes("vicente");

function DeltaBadge({ delta }) {
  const d = delta || 0;
  return (
    <Badge color={d >= 0 ? T.green : T.red} size="sm">
      <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
        {d >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
        {d >= 0 ? "+" : ""}{d.toFixed(1)}
      </span>
    </Badge>
  );
}

function RankingTable({ ranking }) {
  if (!ranking.length) return (
    <div style={{ padding: 40, textAlign: "center", color: T.textLow, fontSize: 13 }}>Sem dados para esta área.</div>
  );

  // Ordenar por posição (menor = melhor), depois por nota desc
  const sorted = [...ranking].sort((a, b) => {
    if (a.posicao && b.posicao) return a.posicao - b.posicao;
    const notaA = a.nota || a.score || 0;
    const notaB = b.nota || b.score || 0;
    return notaB - notaA;
  });

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "44px 1fr 60px 60px 60px 60px", padding: T.tableHeaderPadding, borderBottom: `1px solid ${T.border}`, fontSize: T.tableHeaderFontSize, fontWeight: T.tableHeaderWeight, color: T.textLow, textTransform: "uppercase", letterSpacing: "0.06em", background: T.bgBase }}>
        <span style={{ textAlign: "center" }}>#</span><span>Unidade Hospitalar</span><span style={{ textAlign: "right" }}>Nota</span><span style={{ textAlign: "right" }}>NPS</span><span style={{ textAlign: "right" }}>Δ</span><span style={{ textAlign: "right" }}>Resp.</span>
      </div>
      {sorted.map((r, i) => {
        const eu = isMe(r.unidade);
        const nps = r.nps ?? r.score ?? 0;
        const nota = r.nota ?? 0;
        const pos = i + 1;
        return (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "44px 1fr 60px 60px 60px 60px", padding: T.tableCellPadding, borderBottom: `1px solid ${T.border}`, background: eu ? `${T.amber}08` : "transparent", alignItems: "center", transition: T.transitionFast }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: eu ? T.amber : T.textLow, textAlign: "center" }}>
              {pos}º
            </span>
            <span style={{ fontSize: 13, color: eu ? T.amber : T.textHi, fontWeight: eu ? 700 : 400, display: "flex", alignItems: "center", gap: 6 }}>
              {r.unidade}
              {eu && <Badge color={T.amber} size="sm">VOCÊ</Badge>}
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: nota >= 9 ? T.green : nota >= 8 ? T.accent : nota >= 7 ? T.amber : T.red, textAlign: "right" }}>{nota.toFixed(1)}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: nps >= 75 ? T.green : nps >= 50 ? T.accent : nps >= 30 ? T.amber : T.red, textAlign: "right" }}>{nps.toFixed(1)}</span>
            <span style={{ display: "flex", justifyContent: "flex-end" }}><DeltaBadge delta={r.delta || r.variacao || 0} /></span>
            <span style={{ fontSize: 12, color: T.textLow, textAlign: "right" }}>{(r.respostas || 0).toLocaleString("pt-BR")}</span>
          </div>
        );
      })}
    </>
  );
}

function ViewPorArea({ areas, areasMap }) {
  const [area, setArea] = useState(areas[0] || "");
  const ranking = areasMap[area] || [];
  const minha = ranking.find(r => isMe(r.unidade));

  return (
    <>
      {areas.length > 0 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          {areas.map(a => (
            <button key={a} onClick={() => setArea(a)} style={{ padding: "6px 16px", borderRadius: T.radiusXs, fontSize: T.subtitleSize, fontFamily: T.fontBody, cursor: "pointer", background: area === a ? `${T.accent}20` : "transparent", border: `1px solid ${area === a ? T.accent : T.border}`, color: area === a ? T.accent : T.textLow, transition: T.transitionFast }}>
              {a}
            </button>
          ))}
        </div>
      )}

      {minha && (
        <Card style={{ background: `${T.amber}10`, border: `1px solid ${T.amber}30`, marginBottom: 20, padding: "14px 20px", display: "flex", alignItems: "center", gap: 14 }}>
          <Trophy size={18} color={T.amber} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.amber }}>{minha.unidade || "Sua Unidade"} — {area}</div>
            <div style={{ fontSize: 12, color: T.textLow, marginTop: 2 }}>
              {minha.posicao}º lugar · Nota {(minha.nota ?? 0).toFixed(1)} · NPS {(minha.nps ?? minha.score ?? 0).toFixed(1)} · {(minha.delta || 0) >= 0 ? "+" : ""}{(minha.delta || 0).toFixed(1)} vs anterior
            </div>
          </div>
        </Card>
      )}

      <Card noPadding>
        <RankingTable ranking={ranking} />
      </Card>
    </>
  );
}

function ViewPorUnidade({ areas, areasMap }) {
  const rows = areas.map(area => {
    const ranking = areasMap[area] || [];
    const minha = ranking.find(r => isMe(r.unidade));
    return { area, posicao: minha?.posicao || null, nota: minha?.nota ?? 0, nps: minha?.nps ?? minha?.score ?? 0, delta: minha?.delta || minha?.variacao || 0, respostas: minha?.respostas || 0, total: ranking.length };
  }).filter(r => r.nota || r.nps);

  // Ordenar por posição (melhor primeiro)
  rows.sort((a, b) => (a.posicao || 999) - (b.posicao || 999));

  if (!rows.length) return (
    <div style={{ padding: 40, textAlign: "center", color: T.textLow, fontSize: 13 }}>Sem dados de posição disponíveis.</div>
  );

  return (
    <Card noPadding>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 50px 56px 56px 56px 56px 70px", padding: T.tableHeaderPadding, borderBottom: `1px solid ${T.border}`, fontSize: T.tableHeaderFontSize, fontWeight: T.tableHeaderWeight, color: T.textLow, textTransform: "uppercase", letterSpacing: "0.06em", background: T.bgBase }}>
        <span>Área</span><span style={{ textAlign: "center" }}>Pos.</span><span style={{ textAlign: "right" }}>Nota</span><span style={{ textAlign: "right" }}>NPS</span><span style={{ textAlign: "right" }}>Δ</span><span style={{ textAlign: "right" }}>Resp.</span><span style={{ textAlign: "right" }}>Total</span>
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 50px 56px 56px 56px 56px 70px", padding: T.tableCellPadding, borderBottom: `1px solid ${T.border}`, alignItems: "center", transition: T.transitionFast }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: T.textHi }}>{r.area}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: r.posicao <= 3 ? T.green : r.posicao <= 10 ? T.accent : T.textMid, textAlign: "center" }}>
            {r.posicao}º
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: r.nota >= 9 ? T.green : r.nota >= 8 ? T.accent : r.nota >= 7 ? T.amber : T.red, textAlign: "right" }}>{r.nota.toFixed(1)}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: r.nps >= 75 ? T.green : r.nps >= 50 ? T.accent : r.nps >= 30 ? T.amber : T.red, textAlign: "right" }}>{r.nps.toFixed(1)}</span>
          <span style={{ display: "flex", justifyContent: "flex-end" }}><DeltaBadge delta={r.delta} /></span>
          <span style={{ fontSize: 12, color: T.textLow, textAlign: "right" }}>{r.respostas.toLocaleString("pt-BR")}</span>
          <span style={{ fontSize: 12, color: T.textLow, textAlign: "right" }}>{r.total} unid.</span>
        </div>
      ))}
    </Card>
  );
}

export default function Comparativo() {
  const [rawData, setRawData] = useState(null);
  const [error, setError]     = useState(null);
  const [view, setView]       = useState("area");

  useEffect(() => {
    fetch("/api/comparativo", { credentials: "include" })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`Erro ${r.status}`)))
      .then(d => setRawData(d))
      .catch(e => { console.warn("Comparativo fetch failed:", e.message); setError(e.message); });
  }, []);

  const areasMap = rawData?.allAreas || {};
  const areas    = (rawData?.areas || Object.keys(areasMap)).sort((a, b) => a.localeCompare(b, "pt-BR"));

  if (error) return (
    <PageContainer>
      <div style={{ padding: "16px 20px", background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.2)", borderRadius: T.radiusSm, color: "#f43f5e", fontSize: 13 }}>
        Erro ao carregar comparativo: {error}
      </div>
    </PageContainer>
  );

  return (
    <PageContainer>
      <PageHeader title="Comparativo" description="Ranking NPS" />

      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        <button onClick={() => setView("unidade")} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 18px", borderRadius: T.radiusXs, fontSize: T.subtitleSize, fontFamily: T.fontBody, cursor: "pointer", fontWeight: view === "unidade" ? 600 : 400, background: view === "unidade" ? `${T.accent}18` : "transparent", border: `1px solid ${view === "unidade" ? T.accent : T.border}`, color: view === "unidade" ? T.accent : T.textLow, transition: T.transitionFast }}>
          <BarChart2 size={14} /> Por Area
        </button>
        <button onClick={() => setView("area")} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 18px", borderRadius: T.radiusXs, fontSize: T.subtitleSize, fontFamily: T.fontBody, cursor: "pointer", fontWeight: view === "area" ? 600 : 400, background: view === "area" ? `${T.accent}18` : "transparent", border: `1px solid ${view === "area" ? T.accent : T.border}`, color: view === "area" ? T.accent : T.textLow, transition: T.transitionFast }}>
          <Building2 size={14} /> Por Unidade
        </button>
      </div>

      {!rawData && <PageLoading label="Carregando..." />}

      {rawData && view === "area" && (
        <ViewPorArea areas={areas} areasMap={areasMap} />
      )}
      {rawData && view === "unidade" && (
        <ViewPorUnidade areas={areas} areasMap={areasMap} />
      )}
    </PageContainer>
  );
}
